// apps/frontend_fl/src/pages/configs/cnpjs_restritos/index.tsx
import { INotificationMessage } from "@fl/shared/src/types/messages";
import { ConfiguracoesCnpjsRestritosAttributes } from "@fl/shared/src/types/models/suporte_fl/cnpjs_restritos";
import { ITableDataColumns } from "@fl/shared/src/types/tables";
import { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { BsCircleFill, BsToggleOff, BsToggleOn, BsTrash } from "react-icons/bs";
import AsyncSelect from "react-select/async";
import ConfirmModal from "../../../components/modals/dialog";
import TableData from "../../../components/tables";
import { useNotificationContext } from "../../../context/notifications";
import DefaultBasePage from "../../../pages/default/base";
import ApiCnpjsRestritos from "../../../services/suporte/auth/configs/cnpjs_restritos";
import ApiFornecedores from "../../../services/suporte/auth/fornecedores";
import SortFunctions from "../../../utils/sortArrays";
import { formatCpfCnpj } from "../../../utils/stringsFormats";
import StringsFuncs from "../../../utils/stringsFuncs";

type Option = { value: string; label: string };

interface ICnpjRestritoRow extends ConfiguracoesCnpjsRestritosAttributes {
  fornecedor?: {
    id_contato_con: number;
    st_nome_con?: string;
    st_cpf_con?: string;
    st_cnpj_con?: string;
  };
}

type TFormAdd = {
  id_fornecedores: string[]; // multi seleção
  ativo: boolean; // switch "Ativo"
};

const ConfigCNPJSRestritosPrivatePage = () => {
  const { addNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);
  const [listActivesOnly, setListActivesOnly] = useState(true);

  // Tabela
  const [items, setItems] = useState<ICnpjRestritoRow[]>([]);
  const [filtered, setFiltered] = useState<ICnpjRestritoRow[]>([]);

  // RHF do topo (FornecedoresComponent)
  const { control, handleSubmit, setValue, watch, reset } = useForm<TFormAdd>({
    defaultValues: {
      id_fornecedores: [],
      ativo: true,
    },
  });

  // Cache local de opções já buscadas
  const [fornOptions, setFornOptions] = useState<Option[]>([]);

  // Modal de confirmação (remoção)
  const [showConfirm, setShowConfirm] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<ICnpjRestritoRow | null>(null);

  // Carrega lista
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const result = await new ApiCnpjsRestritos().list(
          `?inativos=${listActivesOnly ? "0" : "1"}`
        );
        if (result.success) {
          const arr: ICnpjRestritoRow[] = (result.data || []).sort(
            (a: ICnpjRestritoRow, b: ICnpjRestritoRow) =>
              new SortFunctions(
                a.fornecedor?.st_nome_con || "",
                b.fornecedor?.st_nome_con || ""
              ).sortStrAsc()
          );
          setItems(arr);
          setFiltered(arr);
        } else if (result.message) {
          setMessages(result.message);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [listActivesOnly]);

  // Busca fornecedores por termo (≥ 2 caracteres)
  const searchFornecedoresByTerm = async (term: string) => {
    const result = await new ApiFornecedores({ term }).find();
    if (result.success) {
      const data = Array.isArray(result.data)
        ? result.data
        : Object.values(result.data || {});

      return data.map((item: any) => ({
        value: String(item.id_contato_con),
        label: `[${new StringsFuncs(item.id_contato_con).formatarNumeroMilhar(
          7
        )}] ${item.st_nome_con ?? ""} - ${formatCpfCnpj(
          item.st_cpf_con ?? item.st_cnpj_con ?? item.st_cpf_con ?? ""
        )}`,
      }));
    }
    return [];
  };

  // Colunas da tabela
  const columnsTable: ITableDataColumns[] = useMemo(
    () => [
      {
        columnKey: "id_restrito",
        label: "#",
        pk: true,
        showColumn: false,
        alignText: "text-center",
      },
      {
        columnKey: "id_fornecedor",
        label: "ID Fornecedor",
        alignText: "text-center",
      },
      {
        columnKey: "fornecedor",
        label: "Fornecedor",
        alignText: "text-start",
        render: (item: ICnpjRestritoRow) => {
          const nm = item.fornecedor?.st_nome_con || "";
          const doc = item.fornecedor?.st_cpf_con
            ? formatCpfCnpj(item.fornecedor.st_cpf_con)
            : item.fornecedor?.st_cnpj_con
              ? formatCpfCnpj(item.fornecedor.st_cnpj_con)
              : "";
          return (
            <div>
              <div className="fw-bold">{nm}</div>
              <div className="text-muted">{doc}</div>
            </div>
          );
        },
        sortValue: (it: ICnpjRestritoRow) =>
          (it.fornecedor?.st_nome_con || "").toLowerCase(),
      },
      {
        columnKey: "ativo",
        label: "Situação",
        alignText: "text-center",
        render: (item: ICnpjRestritoRow) =>
          item.ativo ? (
            <BsCircleFill size={13} color="green" title="Ativo" />
          ) : (
            <BsCircleFill size={13} color="red" title="Inativo" />
          ),
        sortValue: (item: ICnpjRestritoRow) => (item.ativo ? 1 : 0),
      },
      {
        columnKey: "createdAt",
        label: "Criado em",
        alignText: "text-center",
        sortValue: (item: ICnpjRestritoRow) =>
          new Date(item.createdAt || "").getTime(),
      },
      {
        columnKey: "{actions}",
        label: "Ação",
        action: true,
        actionColumnIcons: true,
        alignText: "text-center",
        actionButtons: (item: ICnpjRestritoRow) => {
          const btns = [
            {
              label: item.ativo ? "Inativar" : "Ativar",
              icon: item.ativo ? (
                <BsToggleOn size={20} />
              ) : (
                <BsToggleOff size={20} />
              ),
              iconHover: item.ativo ? (
                <BsToggleOff size={20} />
              ) : (
                <BsToggleOn size={20} />
              ),
              hover: false,
              action: async (row: ICnpjRestritoRow) => {
                setLoading(true);
                try {
                  const payload = { ativo: !row.ativo };
                  const res = await new ApiCnpjsRestritos(payload).update(
                    row.id_restrito?.toString() ?? ""
                  );
                  if (res.success) {
                    setItems((prev) =>
                      prev.map((i) =>
                        i.id_restrito === row.id_restrito
                          ? { ...i, ativo: !i.ativo }
                          : i
                      )
                    );
                    setFiltered((prev) =>
                      prev.map((i) =>
                        i.id_restrito === row.id_restrito
                          ? { ...i, ativo: !i.ativo }
                          : i
                      )
                    );
                    if (res.message) addNotification(res.message[0]);
                  } else if (res.message) {
                    setMessages(res.message);
                  }
                } finally {
                  setLoading(false);
                }
              },
            },
            {
              label: "Remover",
              icon: <BsTrash size={18} />,
              iconHover: <BsTrash size={18} />,
              hover: false,
              action: (row: ICnpjRestritoRow) => {
                setRowToDelete(row);
                setShowConfirm(true);
              },
            },
          ];
          return btns;
        },
      },
    ],
    [addNotification]
  );

  /**
   * Submit do topo: inclui TODOS os fornecedores selecionados.
   */
  const onSubmitAdd = handleSubmit(async ({ id_fornecedores, ativo }) => {
    if (!id_fornecedores || id_fornecedores.length === 0) {
      addNotification({
        key: "cnpj-restrito-sem-fornecedor",
        header: "Fornecedor obrigatório",
        message: "Selecione ao menos um fornecedor para incluir.",
        variant: "warning",
        show: true,
        date: new Date(),
      });
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.all(
        id_fornecedores.map((idStr) =>
          new ApiCnpjsRestritos({
            id_fornecedor: Number(idStr),
            ativo,
          }).create()
        )
      );

      results.forEach((res) => {
        if (res?.success) {
          const novo: ICnpjRestritoRow = res.data;
          setItems((prev) => [novo, ...prev]);
          setFiltered((prev) => [novo, ...prev]);
          if (res.message) addNotification(res.message[0]);
        } else if (res?.message) {
          setMessages((prev) => [...prev, ...res.message!]);
        }
      });

      reset({ id_fornecedores: [], ativo });
      setFornOptions([]);
    } finally {
      setLoading(false);
    }
  });

  /**
   * FornecedoresComponent (revisto)
   */
  const [fornCache, setFornCache] = useState<
    Record<string, { value: string; label: string }>
  >({});

  const FornecedoresComponent = (
    <Controller
      name="id_fornecedores"
      control={control}
      render={({ field: { onChange, value } }) => {
        const selectedForns = (value || [])
          .map((id: string) => fornCache[id])
          .filter(Boolean);

        const handleChangeForns = (selected: any) => {
          const arr: { value: string; label: string }[] = selected || [];
          const ids = arr.map((s) => s.value);

          setFornCache((prev) => {
            const next = { ...prev };
            arr.forEach((opt) => (next[opt.value] = opt));
            return next;
          });

          onChange(ids);
        };

        const loadOptions = async (inputValue: string) => {
          const term = (inputValue || "").trim();
          if (term.length < 2) return [];
          const fetched = await searchFornecedoresByTerm(term);

          setFornCache((prev) => {
            const next = { ...prev };
            fetched.forEach((opt) => {
              if (!next[opt.value]) next[opt.value] = opt;
            });
            return next;
          });

          return fetched; // somente filtrados
        };

        return (
          <AsyncSelect
            isMulti
            cacheOptions
            defaultOptions={selectedForns}
            value={selectedForns}
            loadOptions={loadOptions}
            onChange={handleChangeForns}
            noOptionsMessage={() => "Sem resultados"}
            placeholder="Digite pelo menos 2 caracteres..."
          />
        );
      }}
    />
  );

  // observado para exibir/editar o switch ativo no topo
  const ativoTopo = watch("ativo");

  // Confirmação de exclusão
  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    setLoading(true);
    try {
      const res = await new ApiCnpjsRestritos(rowToDelete).delete();
      if (res.success) {
        setItems((prev) =>
          prev.filter((i) => i.id_restrito !== rowToDelete.id_restrito)
        );
        setFiltered((prev) =>
          prev.filter((i) => i.id_restrito !== rowToDelete.id_restrito)
        );
        if (res.message) addNotification(res.message[0]);
      } else if (res.message) {
        setMessages(res.message);
      }
    } finally {
      setLoading(false);
      setShowConfirm(false);
      setRowToDelete(null);
    }
  };

  const confirmTitle = "Remover CNPJ restrito";
  const confirmMessage = (() => {
    if (!rowToDelete) return "";
    const nome =
      rowToDelete.fornecedor?.st_nome_con ||
      `Fornecedor ID ${rowToDelete.id_fornecedor}`;
    const doc = rowToDelete.fornecedor?.st_cpf_con
      ? formatCpfCnpj(rowToDelete.fornecedor.st_cpf_con)
      : rowToDelete.fornecedor?.st_cnpj_con
        ? formatCpfCnpj(rowToDelete.fornecedor.st_cnpj_con)
        : "";
    return `Confirma remover o fornecedor <b>${nome}</b>${
      doc ? ` (${doc})` : ""
    } da lista de CNPJs restritos?`;
  })();

  return (
    <DefaultBasePage
      title="CNPJs Restritos"
      subtitle={`Registros: ${filtered.length}`}
      loading={loading}
      message={messages}
      filterComp={true}
      dataToFilter={items}
      setFilteredData={setFiltered}
    >
      {/* Form topo (FornecedoresComponent + Ativo + Incluir) */}
      <div className="mb-3">
        <Form onSubmit={onSubmitAdd}>
          <Row className="align-items-end g-2">
            <Col md={9}>
              <Form.Label>Fornecedores (buscar ≥ 2 caracteres):</Form.Label>
              {FornecedoresComponent}
            </Col>

            <Col md={2}>
              <Form.Label>&nbsp;</Form.Label>
              <div>
                <Form.Check
                  type="switch"
                  checked={!!ativoTopo}
                  label={ativoTopo ? "Ativo" : "Inativo"}
                  onChange={() => setValue("ativo", !ativoTopo)}
                />
              </div>
            </Col>

            <Col md={1} className="text-end">
              <Button variant="outline-success" type="submit">
                Incluir
              </Button>
            </Col>
          </Row>
        </Form>

        <Form.Check
          className="mt-3"
          type="switch"
          checked={listActivesOnly}
          label={listActivesOnly ? "Somente ativos" : "Ativos e inativos"}
          onChange={() => setListActivesOnly(!listActivesOnly)}
        />
      </div>

      {/* Tabela */}
      <TableData
        key={JSON.stringify(filtered)}
        columns={columnsTable}
        data={filtered}
      />

      {/* Modal de confirmação */}
      <ConfirmModal
        show={showConfirm}
        onHide={() => {
          if (!loading) {
            setShowConfirm(false);
            setRowToDelete(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        title={confirmTitle}
        message={confirmMessage}
      />
    </DefaultBasePage>
  );
};

export default ConfigCNPJSRestritosPrivatePage;
